import { HttpErrorResponse } from "@angular/common/http";
import { Component, Input, OnChanges, SimpleChanges } from "@angular/core";
import { merge } from "lodash";
import { finalize, map } from "rxjs/operators";
import { environment as appEnv } from "../../../environments/environment";
import { CleanFilePathService } from "../../clean-file/clean-file-path.service";
import { AttributeService } from "../../models/attribute.service";
import { TimeDurAgoService } from "../../time-dur-ago/time-dur-ago.service";
import { pluralize } from "../../utils/pluralize";
import { HotspotDotColorService } from "./dot-colors.service";

enum countType {
  groupCount = "groupCount",
  noticeCount = "noticeCount",
}

interface IAttr {
  groupCount: number;
  id: number;
  noticeCount: number;
  value: any;
}

interface IAttributeType {
  attrKey: string;
  tooltip: (attr: IAttr) => string;
  displayValue: (attr: IAttr) => string;
  countType: countType;
}

interface IHotspot extends IAttr {
  displayValue: string;
  dotColor: string;
}

@Component({
  selector: "ab-attr-hotspots",
  templateUrl: "./attr-hotspots.component.html",
})
export class AttrHotspotsComponent implements OnChanges {
  @Input() public featureDisabled: boolean;
  @Input() public identifier: string;
  @Input() public project: any;
  @Input() public dur: string;
  @Input() public lastN: number;

  public appEnv = appEnv;
  public hotspots: any[];
  public selectedAttributeType: IAttributeType;
  public searchFilterStartTime: number;
  public loading: boolean;
  public showEmptyState: boolean;

  private attributeTypes: IAttributeType[];
  private sampleErrorMessages = [
    "(unknown): Script error",
    "TypeError: 'null' is not an object (evaluating 'event.relatedTarget.parentNode')",
    "TypeError: 'undefined' is not a function (evaluating '$(document)')",
    "TypeError: 'undefined' is not an object (evaluating 'params.length')",
    "TypeError: Object doesn’t support this property or method",
    "Uncaught RangeError: Maximum call stack size exceeded",
    "Uncaught ReferenceError: service is not defined",
    "Uncaught TypeError: Cannot read property 'attr' of undefined",
    "Uncaught TypeError: Cannot read property 'length' of undefined.",
    "Uncaught TypeError: Cannot set property 'value' of undefined",
  ];

  constructor(
    private attributeService: AttributeService,
    private cleanFilePathService: CleanFilePathService,
    private hotspotDotColorService: HotspotDotColorService,
    private timeDurAgoService: TimeDurAgoService,
  ) {
    this.attributeTypes = [
      {
        attrKey: "errors.0.file",
        tooltip(attr: IHotspot) {
          return `${attr.groupCount} ${pluralize(attr.groupCount, "error")}
                  occurred in ${attr.displayValue}`;
        },
        displayValue: (attr: IHotspot) => {
          return this.cleanFilePathService.clean(attr.value.file);
        },
        countType: countType.groupCount,
      },
      {
        attrKey: "errors.0.message",
        tooltip(attr: IHotspot) {
          return `${attr.groupCount} ${pluralize(attr.groupCount, "error")}
                  ${pluralize(attr.groupCount, "has", "have")} this message:
                  ${attr.displayValue}`;
        },
        displayValue(attr: IHotspot) {
          return attr.value.message;
        },
        countType: countType.groupCount,
      },
      {
        attrKey: "context.url",
        tooltip(attr: IHotspot) {
          return `${attr.groupCount} ${pluralize(attr.groupCount, "error")}
                  occurred at ${attr.displayValue}`;
        },
        displayValue(attr: IHotspot) {
          return attr.value.url;
        },
        countType: countType.groupCount,
      },
      {
        attrKey: "context.user",
        tooltip(attr: IHotspot) {
          return `${attr.value.name} saw ${attr.groupCount}
               ${pluralize(attr.groupCount, "error")} with ${attr.noticeCount}
               total ${pluralize(attr.noticeCount, "occurrence")}`;
        },
        displayValue(attr: IHotspot) {
          let value = "";

          if (attr.value.name) {
            value += `${attr.value.name} `;
          }
          if (attr.value.email) {
            value += `<${attr.value.email}> `;
          }
          if (attr.value.id) {
            value += `id=${attr.value.id} `;
          }
          if (attr.value.username) {
            value += `username=${attr.value.username} `;
          }

          return value.trim();
        },
        countType: countType.noticeCount,
      },
    ];
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (this.featureDisabled) {
      this.hotspots = this.generateSampleFileData();
      return;
    }

    if (changes.identifier || changes.dur || changes.lastN) {
      for (const attributeType of this.attributeTypes) {
        if (attributeType.attrKey === this.identifier) {
          this.selectedAttributeType = attributeType;
          break;
        }
      }

      const createdAfter = this.timeDurAgoService.timeFor(this.dur, this.lastN);
      this.searchFilterStartTime = Math.round(
        new Date(createdAfter).getTime() / 1000,
      );

      this.loading = true;
      this.showEmptyState = false;

      this.fetchData(this.selectedAttributeType, createdAfter)
        .pipe(
          finalize(() => {
            this.loading = false;
          }),
        )
        .subscribe(
          (data: IHotspot[]) => {
            this.hotspots = data;
            if (this.hotspots.length === 0) {
              this.showEmptyState = true;
            }
          },
          (_response: HttpErrorResponse) => {
            this.showEmptyState = true;
          },
        );
    }
  }

  public trackBy(_index: number, hotspot: any) {
    return hotspot.id;
  }

  public errorSearchParams(hotspot: any) {
    if (this.featureDisabled) {
      return;
    }

    return {
      projectId: this.project.id,
      filters: {
        resolved: "any",
        start_time: this.searchFilterStartTime,
        [this.selectedAttributeType.attrKey]: hotspot.id,
      },
    };
  }

  private fetchData(attributeType: IAttributeType, createdAfter: string) {
    return this.attributeService
      .query({
        attribute: attributeType.attrKey,
        limit: 10,
        order:
          attributeType.countType === countType.groupCount
            ? "group_count DESC"
            : "notice_count DESC",
        projectId: this.project.id,
        time__gte: createdAfter,
      })
      .pipe(
        map((data: { attributes: IAttr[] | null }) => {
          const attrs = data.attributes || [];
          return this.convertAttrsToHotspots(attrs, attributeType);
        }),
      );
  }

  private convertAttrsToHotspots(attrs: IAttr[], attrType: IAttributeType) {
    let countSum = 0;
    for (const attr of attrs) {
      countSum += attr[attrType.countType];
    }

    return attrs.map((attr) => {
      const dotColor = this.hotspotDotColorService.getDotColor(
        attr[attrType.countType],
        countSum,
        attrs.length,
      );
      const displayValue = attrType.displayValue(attr);
      const count = attr[attrType.countType];
      return merge({}, attr, { dotColor, displayValue, count });
    });
  }

  private generateSampleFileData() {
    const attrs = this.sampleErrorMessages
      .map((message: string, i: number) => {
        return {
          groupCount: this.generateSampleNumber(50),
          id: i,
          noticeCount: this.generateSampleNumber(100),
          value: { message },
        };
      })
      .sort((a, b) => b.groupCount - a.groupCount);
    return this.convertAttrsToHotspots(attrs, this.attributeTypes[1]);
  }

  private generateSampleNumber(max: number): number {
    return Math.round(Math.random() * max);
  }
}
